// Step 7's pre-step: move early evidence onto the node it is really about.
// Market findings (step 2) and assumptions (step 3) are captured before the
// specific needs and capabilities exist, so they attach to the idea by
// default. This scans for that stranded evidence and proposes a better home
// for each; the PM moves it or keeps it on the idea (propose-then-promote).
// Doing this first makes the tension report below more accurate.

import { useState } from 'react';
import { Anchor, Radar, Loader2, CornerDownRight, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { useReattach, useGenerateReattach, useApplyReattach, useDismissReattach } from '@/hooks/use-reattach';
import { useCanvasData } from '@/hooks/use-canvas-data';
import { findingTypeLabel } from '@/lib/market-categories';
import { NODE_LABEL } from '@/lib/node-visuals';
import type { NodeMetadata } from '@/services/canvas-mappers';
import { toast } from '@/hooks/use-toast';
import type { ReattachProposal } from '@/types/api';

export function ReattachSection({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const { data: row, isLoading, isError } = useReattach(projectId);
  const generate = useGenerateReattach(projectId);
  const apply = useApplyReattach(projectId);
  const dismiss = useDismissReattach(projectId);
  const { data: canvas } = useCanvasData(projectId);

  // Rows hide their rationale by default (the title and target are usually
  // enough to decide); this broadcast lets the list header open or close every
  // row at once, the same pattern the other evidence lists use.
  const [expandAll, setExpandAll] = useState(false);
  const [expandSeq, setExpandSeq] = useState(0);

  // Research findings read their category ("Competitor finding") by looking the
  // evidence node up in canvas data, so the stored proposal needs no new field
  // and a finding re-tagged later reads correctly. A node deleted between scan
  // and render falls back to the generic label.
  const labelFor = (p: ReattachProposal): string => {
    if (p.evidenceType === 'assumption') return 'Assumption';
    const node = canvas?.nodes.find((n) => n.id === p.evidenceId);
    return findingTypeLabel((node?.data as { metadata?: NodeMetadata } | undefined)?.metadata);
  };

  const proposed = (row?.proposals ?? []).filter((p) => p.status === 'proposed');
  const busy = apply.isPending || dismiss.isPending || generate.isPending;

  const onGenerate = async () => {
    try {
      await generate.mutateAsync();
    } catch (err) {
      toast({ title: "Couldn't check for evidence", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };
  const onApply = async (id: string) => {
    try {
      await apply.mutateAsync(id);
    } catch (err) {
      toast({ title: "Couldn't move it", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };
  const onDismiss = async (id: string) => {
    try {
      await dismiss.mutateAsync(id);
    } catch (err) {
      toast({ title: "Couldn't keep it", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };

  return (
    <section className="rounded-lg border bg-card p-5 space-y-3">
      <h2 className="font-semibold flex items-center gap-2">
        <Anchor className="h-4 w-4 text-muted-foreground" />
        Move early evidence to where it belongs
      </h2>
      <p className="text-sm text-muted-foreground">
        Findings and assumptions the AI produced early sit on the idea until something more specific
        exists. Move each one to the need or capability it's really about, or keep it where it is.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {isError && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Couldn't load suggestions. Check again to retry.
            </div>
          )}
          {proposed.length > 0 && (
            <div className="space-y-2">
              {/* The normaliser allows a proposal with no rationale, so the
                  toggle only appears when at least one row has a reason to show. */}
              {proposed.some((p) => p.rationale) && (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={() => {
                    setExpandAll(!expandAll);
                    setExpandSeq((v) => v + 1);
                  }}
                >
                  {expandAll ? 'Hide reasons' : 'Show reasons'}
                </Button>
              </div>
              )}
              <ul className="space-y-2">
                {proposed.map((p) => (
                  <ProposalRow
                    key={p.id}
                    proposal={p}
                    evidenceLabel={labelFor(p)}
                    canEdit={canEdit}
                    busy={busy}
                    onApply={onApply}
                    onDismiss={onDismiss}
                    expandSignal={expandSeq}
                    expandTo={expandAll}
                  />
                ))}
              </ul>
            </div>
          )}

          {row && proposed.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {row.strandedCountAtRun === 0
                ? 'Nothing the AI produced is still sitting on the idea.'
                : 'Nothing left to move.'}
            </p>
          )}

          {canEdit && (
            <Button size="sm" variant={proposed.length > 0 ? 'ghost' : 'default'} disabled={generate.isPending || busy} onClick={onGenerate}>
              {generate.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Checking…
                </>
              ) : (
                <>
                  <Radar className="h-3.5 w-3.5 mr-1.5" />
                  {row ? 'Check again' : 'Check for evidence to move'}
                </>
              )}
            </Button>
          )}
        </>
      )}
    </section>
  );
}

// One suggestion: what the evidence is, where it should go, and (on demand)
// why. Rows sit on the section's own surface with both ends typed in the
// node's canvas hue. Evidence and target each get a line: finding titles run
// long, and a single wrapping row left the arrow dangling at the end of line
// one with the target reading like a second finding.
function ProposalRow({
  proposal: p,
  evidenceLabel,
  canEdit,
  busy,
  onApply,
  onDismiss,
  expandSignal,
  expandTo,
}: {
  proposal: ReattachProposal;
  evidenceLabel: string;
  canEdit: boolean;
  busy: boolean;
  onApply: (id: string) => void;
  onDismiss: (id: string) => void;
  expandSignal: number;
  expandTo: boolean;
}) {
  // A row follows the list's last broadcast unless it was toggled since. Keyed
  // on the broadcast sequence so a new "show all" wins over an older per-row
  // click, with no effect needed to sync the two.
  const [toggled, setToggled] = useState<{ seq: number; open: boolean } | null>(null);
  const expanded = toggled && toggled.seq === expandSignal ? toggled.open : expandTo;
  const hasReason = !!p.rationale;

  // Content on the left, the two actions on the right, so the only glyphs in
  // the content column are the chevron and the turned corner that says "sits
  // under". Text-only buttons: a tick and a cross beside a straight arrow made
  // four grey symbols per row and the arrow, the one that carries meaning,
  // was the least distinctive of them.
  return (
    <li className="rounded-md border p-3 text-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1.5">
          {/* Chip and title flow as inline text so a long title wraps after the
              chip instead of dropping to its own line as one flex item. */}
          <div className="flex items-start gap-2">
            {hasReason ? (
              <button
                type="button"
                onClick={() => setToggled({ seq: expandSignal, open: !expanded })}
                className="mt-0.5 shrink-0 text-muted-foreground"
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide reason' : 'Show reason'}
              >
                {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            ) : (
              <span className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
            <p className="min-w-0 leading-snug">
              <Badge tone={p.evidenceType} className="mr-2 align-middle">
                {evidenceLabel}
              </Badge>
              <span className="align-middle">{p.evidenceTitle || '(untitled)'}</span>
            </p>
          </div>
          <div className="flex items-start gap-2 pl-5">
            <CornerDownRight className="mt-0.5 h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <p className="min-w-0 leading-snug">
              <Badge tone={p.targetType as BadgeTone} className="mr-2 align-middle">
                {NODE_LABEL[p.targetType] ?? p.targetType}
              </Badge>
              <span className="align-middle">{p.targetTitle || '(untitled)'}</span>
            </p>
          </div>
          {expanded && hasReason && <p className="text-sm text-foreground pl-5">{p.rationale}</p>}
        </div>
        {/* Stacked, so the actions cost the row one button's width rather
            than two and long titles keep their line. */}
        {canEdit && (
          <div className="flex shrink-0 flex-col items-stretch gap-1">
            <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => onApply(p.id)}>
              Move
            </Button>
            <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={() => onDismiss(p.id)}>
              Keep on idea
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}
